import Phaser from 'phaser';

export interface OrganelleConfig {
  // Visual properties
  color: number;
  alpha: number;
  outline: boolean;
  outlineColor: number;
  
  // Behavior properties
  movementDelay: number; // 0-1, higher = more delay
  wobbleAmount: number; // 0-1, how much random movement
  maintainPosition: boolean; // When true, organelle will try to stay at its assigned position
  
  // Rendering properties
  detailLevel: 'minimal' | 'low' | 'medium' | 'high'; // For different scene requirements
  depth: number; // Z-index for organelle layering - higher values appear on top
  
  // Metadata
  type: string; // 'nucleus', 'mitochondrion', etc.
  id: string; // Unique identifier for network sync
}

export class Organelle extends Phaser.GameObjects.Container {
  // Configuration
  protected config: OrganelleConfig;
  
  // Physics properties
  protected velocity: Phaser.Math.Vector2;
  protected targetPosition: Phaser.Math.Vector2;
  protected lastCellPosition: Phaser.Math.Vector2;
  protected initialPosition: Phaser.Math.Vector2; // Store initial position for reference
  
  // Movement state
  protected movementTimer: number = 0;
  protected wobbleTimer: number = 0;
  protected wobbleOffset: Phaser.Math.Vector2;
  
  // Visual elements
  protected mainShape: Phaser.GameObjects.Graphics;
  protected detailElements: Phaser.GameObjects.GameObject[] = [];

  // Cooldown system for newly created organelles
  protected creationTimestamp: number = 0;
  protected productionCooldownPeriod: number = 3000; // 3 seconds default cooldown

  // Default configuration
  private static readonly DEFAULT_CONFIG: OrganelleConfig = {
    color: 0xAAAAAA,
    alpha: 0.8,
    outline: true,
    outlineColor: 0x000000,
    movementDelay: 0.3,
    wobbleAmount: 0.2,
    maintainPosition: false,
    detailLevel: 'medium',
    depth: 95, // Default depth (below cell membrane at 100)
    type: 'generic',
    id: ''
  };
  
  constructor(scene: Phaser.Scene, x: number, y: number, config?: Partial<OrganelleConfig>) {
    super(scene, x, y);
    
    // Set creation timestamp for cooldown tracking
    this.creationTimestamp = scene ? scene.time.now : Date.now();
    
    // Merge provided config with defaults
    this.config = {
      ...Organelle.DEFAULT_CONFIG,
      ...config
    };
    
    // Ensure we have a valid ID - prioritize the one provided in config
    if (!this.config.id) {
      this.config.id = `org_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
      console.log(`Generated local ID for ${this.config.type} organelle: ${this.config.id}`);
    } else {
      console.log(`Using provided ID for ${this.config.type} organelle: ${this.config.id}`);
    }
    
    // Initialize physics vectors
    this.velocity = new Phaser.Math.Vector2(0, 0);
    this.targetPosition = new Phaser.Math.Vector2(x, y);
    this.lastCellPosition = new Phaser.Math.Vector2(x, y);
    this.wobbleOffset = new Phaser.Math.Vector2(0, 0);
    this.initialPosition = new Phaser.Math.Vector2(x, y); // Initialize with starting position
    
    // Special handling for mitochondria - make sure they stay exactly where placed
    if (this.config.type === 'mitochondrion' && this.config.maintainPosition) {
      // Log that we're enforcing a fixed position
      console.log(`Fixing mitochondrion at (${x}, ${y})`);
      
      // Ensure wobbble is disabled
      this.config.wobbleAmount = 0;
      
      // Set higher movement delay for stability
      this.config.movementDelay = 0.95;
      
      // Store the initial position for reference
      this.initialPosition = new Phaser.Math.Vector2(x, y);
    }
    
    // Create main shape (to be overridden by specific organelles)
    this.mainShape = scene.add.graphics();
    this.add(this.mainShape);
    
    // Make it interactive for hovering effects but not for clicks
    this.setInteractive(new Phaser.Geom.Circle(0, 0, 20), Phaser.Geom.Circle.Contains);
    
    // Set the pointer to pass-through mode so it doesn't block clicks to the scene
    if (this.input) {
      // Cast to any to access non-standard properties
      (this.input as any).alwaysEnabled = true;  // We still want hover events
      this.input.cursor = 'pointer';    // Show pointer cursor on hover
    }
    
    // IMPORTANT: Make organelles ignore input clicks, so clicks pass through to the scene
    // This allows the cell movement to work when clicking near organelles
    this.disableInteractive();
    
    // For mitochondria, completely remove any pointer interaction
    if (this.config.type === 'mitochondrion') {
      this.removeInteractive();
      this.input = null;
    }
    
    // Set depth based on config (membrane is at 100)
    // Different types of organelles can have different depths
    this.setDepth(this.config.depth);
    
    // Add to scene
    scene.add.existing(this);
    
    // With a small delay, do the initial draw
    // This allows subclasses to fully initialize before drawing
    this.scene.time.delayedCall(1, () => {
      this.draw();
    });
  }
  
  // Method to be overridden by child classes
  protected draw(): void {
    this.mainShape.clear();
    
    // Draw a simple circle as fallback
    this.mainShape.fillStyle(this.config.color, this.config.alpha);
    if (this.config.outline) {
      this.mainShape.lineStyle(2, this.config.outlineColor, 1);
    }
    this.mainShape.fillCircle(0, 0, 20);
    if (this.config.outline) {
      this.mainShape.strokeCircle(0, 0, 20);
    }
  }
  
  // Update method called by parent Cell
  update(
    delta: number, 
    cellX: number, 
    cellY: number, 
    cellVelocity?: Phaser.Math.Vector2, 
    resources?: { glucose: number, atp: number, maxATP: number, isOwnCell?: boolean }
  ): void {
    // Safety check: if scene is undefined, the object is likely being destroyed
    if (!this.scene) {
      console.warn(`Cannot update organelle: scene reference is missing (type: ${this.config.type}, id: ${this.config.id})`);
      return;
    }
    
    // Specific handling for mitochondria to ensure they remain fixed but with subtle movement
    if (this.config.type === 'mitochondrion' && this.config.maintainPosition) {
      // Update timers for effects
      this.movementTimer += delta * 0.001;
      this.wobbleTimer += delta * 0.001;
      
      // Apply minimal wobble if wobbleAmount > 0
      if (this.config.wobbleAmount > 0) {
        // Create a very gentle, subtle wobble effect
        const wobbleAmount = this.config.wobbleAmount * 3;
        
        // Use perlin-like noise for natural movement
        const wobbleX = Math.sin(this.wobbleTimer * 1.1) * Math.cos(this.wobbleTimer * 0.7) * wobbleAmount;
        const wobbleY = Math.cos(this.wobbleTimer * 0.8) * Math.sin(this.wobbleTimer * 1.3) * wobbleAmount;
        
        // Apply wobble directly to position with very small magnitude
        this.x += (wobbleX - this.wobbleOffset.x) * 0.1;
        this.y += (wobbleY - this.wobbleOffset.y) * 0.1;
        
        // Store current wobble for next frame
        this.wobbleOffset.set(wobbleX, wobbleY);
      }
      
      // Very gentle drift toward stable position if cell is moving
      // This adds subtle but pleasing organic movement during cell movement
      const cellMoved = (cellX !== this.lastCellPosition.x || cellY !== this.lastCellPosition.y);
      if (cellMoved && cellVelocity && cellVelocity.length() > 2) {
        // Calculate a very small adjustment based on cell movement
        const driftFactor = 0.003; // Slightly increased for more noticeable but still subtle movement
        
        // Calculate a target that's slightly offset in the direction of cell movement
        const targetX = this.initialPosition.x + (cellVelocity.x * driftFactor);
        const targetY = this.initialPosition.y + (cellVelocity.y * driftFactor);
        
        // Calculate distance to this adjusted position
        const dx = targetX - this.x;
        const dy = targetY - this.y;
        
        // Only apply if the distance is significant (prevents micro-adjustments)
        if (Math.sqrt(dx*dx + dy*dy) > 1) {
          // Apply gentle movement toward corrected position
          this.x += dx * 0.02; // Slightly increased for more noticeable but still subtle movement
          this.y += dy * 0.02;
        }
      }
      
      // Update lastCellPosition for next frame
      this.lastCellPosition.set(cellX, cellY);
      return;
    }
    
    // Continue with normal update for other organelles
    // Update timers for effects
    this.movementTimer += delta * 0.001;
    this.wobbleTimer += delta * 0.001;
    
    // Apply wobble
    this.updateWobble();
    
    // Track if cell has moved
    // Note: cellX/cellY are relative to cell center (normally 0,0) since organelles are children of the cell
    const cellMoved = (cellX !== this.lastCellPosition.x || cellY !== this.lastCellPosition.y);
    
    if (cellMoved) {
      // When cell moves, update target and ensure organelle follows more closely
      this.targetPosition.set(cellX, cellY);
      
      // Apply a stronger force for tracking the cell's movement
      // This ensures organelles stay more tightly with the cell
      const delay = this.config.movementDelay;
      
      // For fast cell movement, organelles should stay more closely attached
      const cellSpeed = cellVelocity ? Math.sqrt(cellVelocity.x * cellVelocity.x + cellVelocity.y * cellVelocity.y) : 0;
      const forceScaling = Math.min(1, 0.2 + cellSpeed * 0.02); // Scale force up with cell speed
      
      // More immediate tracking for fast cell movement, preserving internal physics for slow movement
      if (cellSpeed > 5) {
        // Directly move closer to the cell's center with movement delay factor
        const moveX = (cellX - this.x) * (1 - delay * 0.7);
        const moveY = (cellY - this.y) * (1 - delay * 0.7);
        
        this.x += moveX * forceScaling;
        this.y += moveY * forceScaling;
        
        // Reset velocity to prevent accumulation
        this.velocity.scale(0.5);
      } else {
        // For slower cell movement, use physics-based approach
        if (cellVelocity) {
          // Set velocity directly proportional to cell velocity
          this.velocity.x = cellVelocity.x * (1 - delay);
          this.velocity.y = cellVelocity.y * (1 - delay);
        }
      }
    }
    
    // Normal physics-based movement for slower/smaller movements
    this.updatePosition(delta);
    
    // Remember cell position for next frame
    this.lastCellPosition.set(cellX, cellY);
  }
  
  private updateWobble(): void {
    if (this.config.wobbleAmount > 0) {
      // Create a gentle, random wobble effect
      const wobbleAmount = this.config.wobbleAmount * 5;
      
      // Use perlin-like noise for natural movement
      const wobbleX = Math.sin(this.wobbleTimer * 2.1) * Math.cos(this.wobbleTimer * 1.7) * wobbleAmount;
      const wobbleY = Math.cos(this.wobbleTimer * 1.8) * Math.sin(this.wobbleTimer * 2.3) * wobbleAmount;
      
      this.wobbleOffset.set(wobbleX, wobbleY);
    }
  }
  
  private updatePosition(delta: number): void {
    // Normalize delta to ensure consistent movement
    const normalizedDelta = delta / 16.67; // 60 FPS = 16.67ms
    
    // If this organelle should maintain its position, keep it fixed
    if (this.config.maintainPosition) {
      // For mitochondria, completely freeze position - no wobble, no movement
      if (this.config.type === 'mitochondrion') {
        // Check if the position has changed from initial position
        if (this.x !== this.initialPosition.x || this.y !== this.initialPosition.y) {
          // Reset to the initial position if it has moved
          this.setPosition(this.initialPosition.x, this.initialPosition.y);
          this.targetPosition.set(this.initialPosition.x, this.initialPosition.y);
          this.velocity.reset();
          console.log(`Restored mitochondrion to initial position (${this.initialPosition.x}, ${this.initialPosition.y})`);
        }
        // Do not change position at all
        return;
      }
      
      // For other organelles with maintainPosition, apply wobble if enabled
      if (this.config.wobbleAmount > 0) {
        const wobbledX = this.x + this.wobbleOffset.x;
        const wobbledY = this.y + this.wobbleOffset.y;
        
        // Check if wobble would push the organelle too far from center
        const distanceFromCenter = Math.sqrt(wobbledX * wobbledX + wobbledY * wobbledY);
        const maxAllowedDistance = this.config.type === 'nucleus' ? 5 : 70; // Very tight constraint for nucleus
        
        if (distanceFromCenter > maxAllowedDistance) {
          // Scale back the position to stay within bounds
          const angle = Math.atan2(wobbledY, wobbledX);
          const newX = Math.cos(angle) * maxAllowedDistance;
          const newY = Math.sin(angle) * maxAllowedDistance;
          this.setPosition(newX, newY);
        } else {
          this.setPosition(wobbledX, wobbledY);
        }
      }
      return;
    }
    
    // Standard physics-based movement for organelles that don't need to maintain position
    // Calculate direction to target
    const dx = this.targetPosition.x - this.x;
    const dy = this.targetPosition.y - this.y;
    
    // Calculate current distance from cell center
    const currentDistance = Math.sqrt(dx * dx + dy * dy);
    
    // Maximum allowed distance from center - use a tighter constraint for nucleus
    // This is a "soft" constraint that increases force as distance increases
    const maxDistance = this.config.type === 'nucleus' ? 5 : 70; // Reduced for nucleus
    const constraintForce = Math.max(0, (currentDistance / maxDistance) - 0.8);
    
    // Apply stronger attraction when getting too far from center
    const baseForceFactor = this.config.type === 'nucleus' ? 0.05 : 0.02;
    const forceFactor = baseForceFactor * (1 + constraintForce * 5);
    
    // Apply gentle force toward target (stronger if too far)
    this.velocity.x += dx * forceFactor;
    this.velocity.y += dy * forceFactor;
    
    // Apply drag
    const drag = 0.9;
    this.velocity.x *= Math.pow(drag, normalizedDelta);
    this.velocity.y *= Math.pow(drag, normalizedDelta);
    
    // Apply speed limit if moving too fast 
    const speed = Math.sqrt(this.velocity.x * this.velocity.x + this.velocity.y * this.velocity.y);
    const maxSpeed = this.config.type === 'nucleus' ? 2 : 3; // Lower max speed for nucleus
    
    if (speed > maxSpeed) {
      this.velocity.scale(maxSpeed / speed);
    }
    
    // Move organelle
    this.x += this.velocity.x * normalizedDelta;
    this.y += this.velocity.y * normalizedDelta;
    
    // Apply wobble offset to get final position
    const wobbledX = this.x + this.wobbleOffset.x;
    const wobbledY = this.y + this.wobbleOffset.y;
    
    // Hard constraint - if beyond maximum distance after movement, force back inside
    const distanceFromCenter = Math.sqrt(wobbledX * wobbledX + wobbledY * wobbledY);
    
    // Absolute maximum distance - enforce strictly
    const hardMaxDistance = maxDistance * 0.98; // Even stricter than the attraction force
    
    if (distanceFromCenter > hardMaxDistance) {
      // Calculate angle to cell center
      const angle = Math.atan2(wobbledY, wobbledX);
      
      // Set position at maximum allowed distance in same direction
      const newX = Math.cos(angle) * hardMaxDistance;
      const newY = Math.sin(angle) * hardMaxDistance;
      
      // Apply corrected position
      this.setPosition(newX, newY);
      
      // Apply stronger velocity dampening when hitting constraint
      this.velocity.scale(0.5); // More aggressive dampening (0.7 -> 0.5)
      
      // Log when organelles reach constraint (only occasionally to avoid spam)
      if (Math.random() < 0.05) { // 5% chance of logging
        console.log(`${this.config.type} hit constraint: distance=${distanceFromCenter.toFixed(1)}, maxDist=${hardMaxDistance}, speed=${speed.toFixed(1)}`);
      }
    } else {
      // Apply normal position with wobble
      this.setPosition(wobbledX, wobbledY);
    }
  }
  
  // Change detail level based on scene requirements
  setDetailLevel(level: 'minimal' | 'low' | 'medium' | 'high'): void {
    if (this.config.detailLevel !== level) {
      this.config.detailLevel = level;
      this.draw(); // Redraw with new detail level
    }
  }
  
  getType(): string {
    return this.config.type;
  }
  
  getId(): string {
    return this.config.id;
  }
  
  getConfig(): OrganelleConfig {
    return { ...this.config }; // Return a copy to prevent external modification
  }
  
  // For network serialization
  getState(): any {
    return {
      id: this.config.id,
      type: this.config.type,
      x: this.x,
      y: this.y,
      color: this.config.color
    };
  }
  
  // Update from network state
  updateFromState(state: any): void {
    // Only update position if state includes coordinates
    if (state.x !== undefined && state.y !== undefined) {
      this.targetPosition.set(state.x, state.y);
    }
    
    // Update other properties as needed
    if (state.color !== undefined && state.color !== this.config.color) {
      this.config.color = state.color;
      this.draw();
    }
  }

  /**
   * Check if the organelle has completed its initial cooldown period
   * This is used to prevent newly created organelles from immediately producing resources
   * @returns true if cooldown period has elapsed, false if still in cooldown
   */
  public isCooldownComplete(): boolean {
    const currentTime = this.scene ? this.scene.time.now : Date.now();
    return (currentTime - this.creationTimestamp) >= this.productionCooldownPeriod;
  }

  /**
   * Get the creation timestamp of this organelle
   */
  public getCreationTimestamp(): number {
    return this.creationTimestamp;
  }

  /**
   * Get the time remaining in the cooldown period (in milliseconds)
   * @returns Number of milliseconds remaining in cooldown, or 0 if cooldown is complete
   */
  public getCooldownTimeRemaining(): number {
    if (this.isCooldownComplete()) {
      return 0;
    }
    
    const currentTime = this.scene ? this.scene.time.now : Date.now();
    return Math.max(0, this.productionCooldownPeriod - (currentTime - this.creationTimestamp));
  }
} 